import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Checkbox } from '@crazyegginc/hatch';
import { Modal } from '/src/contexts/modal';

import { ReactComponent as PlusIcon } from '@crazyegginc/hatch/dist/images/icon-plus.svg';
import { ReactComponent as ArrowExpandIcon } from '@crazyegginc/hatch/dist/images/icon-arrow-expand.svg';
import { useAuthContext, useManageSubscriptionPath, useModal, useMutation } from '/src/hooks';
import { SOURCE_TYPE, useSelectedSource } from '/src/components/source-selector/source-selector';
import classNames from 'classnames';
import { setEnabledGaProperties } from '../mutations';
import { useLocation } from 'react-router-dom';
import { inflect } from '/src/utils/string';
import { SupportLinks } from '/src/support';
import { globalFeatureKeys } from '/src/features/_global/queries';
import { AppLink } from '/src/features/_global/components/AppLink';
import { SupportLink } from '/src/features/_global/components/SupportLink';

export function ManageGaPropertiesModal() {
  const location = useLocation();
  const modal = useModal();
  const queryClient = useQueryClient();
  const { selectedSource, setSelectedSource } = useSelectedSource();
  const { currentAccount, capabilities } = useAuthContext();
  const { gaProperties } = currentAccount;
  const [expanded, setExpanded] = useState([]);
  const [enabledProperties, setEnabledProperties] = useState(() =>
    gaProperties.filter((p) => !!p.enabledAt || !!p.isNew).map((p) => p.id),
  );
  const initialEnabledPropertiesRef = useRef(gaProperties.filter((p) => !!p.enabledAt || !!p.isNew).map((p) => p.id));
  const overLimitBannerRef = useRef(null);
  const enabledSet = useMemo(() => new Set(enabledProperties), [enabledProperties]);
  const [didSubmit, setDidSubmit] = useState(false);

  const { mutateAsync, isPending } = useMutation(setEnabledGaProperties);

  const groupedProperties = useMemo(() => Object.groupBy(gaProperties, ({ accountId }) => accountId), [gaProperties]);
  const subscriptionPath = useManageSubscriptionPath();

  const propertiesQuota = capabilities?.ga?.quotas?.totalProperties ?? 0;
  const isConstrained = propertiesQuota.limit < 100;

  const isOverLimit = useMemo(() => {
    return enabledProperties.length > propertiesQuota.limit;
  }, [enabledProperties.length, propertiesQuota.limit]);

  useEffect(() => {
    const banner = overLimitBannerRef.current;
    if (!didSubmit || !isOverLimit || !banner) return;

    requestAnimationFrame(() => {
      const { top, bottom } = banner.getBoundingClientRect();
      const outOfView = top < 0 || bottom > window.innerHeight;

      if (outOfView) {
        banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }, [didSubmit, isOverLimit, overLimitBannerRef]);

  const googleAnalyticsRedirect = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set('modal', 'manage_ga');

    const redirectUrl = encodeURIComponent(`${window.SHELL_APP_URL}${location.pathname}?${params.toString()}`);

    window.location.href = `${window.AUTH_API_URL}/ga/import?redirect_to=${redirectUrl}`;
  }, [location.pathname, location.search]);

  const toggleProperty = useCallback((id) => {
    setEnabledProperties((curr) => {
      if (curr.includes(id)) {
        return curr.filter((c) => c !== id);
      }
      return [...curr, id];
    });
  }, []);

  const toggleAccount = useCallback(
    (accountId) => {
      const propertyIds = (groupedProperties?.[accountId] ?? []).map((p) => p.id);

      setEnabledProperties((curr) => {
        const allChecked = propertyIds.every((id) => curr.includes(id));
        const someChecked = propertyIds.some((id) => curr.includes(id));

        if (someChecked && !allChecked) {
          // partially selected > select account
          const merged = new Set([...curr, ...propertyIds]);
          return Array.from(merged);
        }

        if (allChecked) {
          // all selected > unselect account
          return curr.filter((id) => !propertyIds.includes(id));
        }

        // none selected > select all
        const merged = new Set([...curr, ...propertyIds]);
        return Array.from(merged);
      });
    },
    [groupedProperties],
  );

  const save = useCallback(async () => {
    const previousEnabledProperties = initialEnabledPropertiesRef.current;
    const changedPropertyIds = [
      ...enabledProperties.filter((id) => !previousEnabledProperties.includes(id)),
      ...previousEnabledProperties.filter((id) => !enabledProperties.includes(id)),
    ];
    const result = await mutateAsync({ propertyIds: enabledProperties });
    if (result.setEnabledGaProperties) {
      if (changedPropertyIds.length > 0) {
        await queryClient.refetchQueries({ ...globalFeatureKeys.me });
        const meData = queryClient.getQueryData(globalFeatureKeys.me.queryKey);

        const nextAccount = meData?.me?.accounts?.find((account) => account.isCurrent);
        const firstEnabledProperty = (nextAccount?.gaProperties ?? []).find((property) => property.enabledAt);
        const firstActiveSite = (nextAccount?.sites ?? []).find((site) => site.active);
        const selectedProperty = (nextAccount?.gaProperties ?? []).find(
          (property) => String(property.id) === String(selectedSource?.id),
        );
        const activePropertyDisabled = selectedSource?.type === SOURCE_TYPE.GA_PROPERTY && !selectedProperty?.enabledAt;

        if (activePropertyDisabled) {
          if (firstActiveSite) {
            setSelectedSource(firstActiveSite);
          } else if (firstEnabledProperty) {
            setSelectedSource({ ...firstEnabledProperty, type: SOURCE_TYPE.GA_PROPERTY });
          }
        } else if (firstEnabledProperty) {
          setSelectedSource({ ...firstEnabledProperty, type: SOURCE_TYPE.GA_PROPERTY });
        } else if (firstActiveSite) {
          setSelectedSource(firstActiveSite);
        }
      }
      modal.close();
    }
  }, [enabledProperties, modal, mutateAsync, queryClient, setSelectedSource, selectedSource?.id, selectedSource?.type]);

  return (
    <Modal dialogClassName="w-[655px] flex flex-col p-[30px]" closeButtonClassName="!top-[30px] !right-[30px]">
      <h2 className="text-header-3 mb-2">Connect Crazy Egg with Google Analytics</h2>

      {isConstrained ? (
        <span className="text-body-2 mb-6">
          Select up to {propertiesQuota.limit} Google Analytics properties to sync data.{' '}
          <AppLink to={subscriptionPath} className="text-link" target="_blank" rel="noopener noreferrer">
            Upgrade to Connect More
          </AppLink>
        </span>
      ) : (
        <span className="text-body-2 mb-6">
          Add or select a Google Analytics account and properties to pull data from GA4.
        </span>
      )}

      {didSubmit && isOverLimit ? (
        <div ref={overLimitBannerRef}>
          <Banner title="Property limit reached" className="mb-6">
            {isConstrained ? (
              <>
                You can sync up to {propertiesQuota.limit} Google Analytics properties on your current plan. You
                currently have {enabledProperties.length} enabled. Please select {propertiesQuota.limit} or fewer to
                save any changes.{' '}
                <AppLink
                  to={subscriptionPath}
                  className="underline cursor-pointer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Upgrade
                </AppLink>{' '}
                to connect more.
              </>
            ) : (
              <>
                You can sync up to {propertiesQuota.limit} Google Analytics properties on your current plan. Please
                select {propertiesQuota.limit} or fewer to save any changes.
              </>
            )}
          </Banner>
        </div>
      ) : null}

      <button
        onClick={googleAnalyticsRedirect}
        className="w-full flex items-center space-x-2 p-4 rounded border border-mystic-500 text-dodger-blue-500 font-semibold text-sm hover:bg-solitude-500 mb-6"
      >
        <PlusIcon className="fill-current w-4 h-4" />
        <span className="mt-px">Add Google Analytics account</span>
      </button>

      <h4 className="font-semibold text-lynch-500 text-sm mb-2">Google Analytics Accounts</h4>

      <div className="flex flex-col space-y-4">
        {Object.entries(groupedProperties).map(([accountId, properties], idx) => {
          const isExpanded = expanded.includes(idx);

          const enabledInAccCount = properties.reduce((acc, p) => acc + (enabledSet.has(p.id) ? 1 : 0), 0);

          const allChecked = enabledInAccCount === properties.length;
          const someChecked = enabledInAccCount > 0 && enabledInAccCount < properties.length;
          const someHasError = properties.some((p) => !!p.lastError);

          return (
            <div className="flex flex-col" key={`ga_${accountId}`}>
              <div
                className={classNames('border border-solid border-mystic-500 flex flex-col items-center w-full p-4', {
                  rounded: !isExpanded,
                  'rounded-t [border-bottom-style:dashed]': isExpanded,
                })}
              >
                <div className="flex w-full">
                  <div className="flex flex-1 items-center space-x-5">
                    <div>
                      <Checkbox
                        checkBoxClass={classNames({
                          '!border-carnation-500': someHasError,
                          '!bg-carnation-500': someHasError && allChecked,
                        })}
                        checked={allChecked}
                        indeterminate={!allChecked && someChecked}
                        onChange={() => toggleAccount(accountId)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <h5 className="text-body-1">{properties[0].accountName}</h5>
                      <span className="text-[#68768D] text-sm">
                        {enabledInAccCount} of {properties.length}{' '}
                        {inflect('property', properties.length, 'properties')} enabled
                      </span>
                    </div>
                  </div>
                  <button
                    className="p-2"
                    onClick={() =>
                      setExpanded((curr) => {
                        if (curr.includes(idx)) {
                          return curr.filter((c) => c !== idx);
                        }
                        return [...curr, idx];
                      })
                    }
                  >
                    <ArrowExpandIcon
                      aria-label={isExpanded ? 'click to collapse' : 'click to expand'}
                      className={classNames('w-3 h-3 text-cadet-blue-500 fill-current', {
                        'rotate-90': !isExpanded,
                        'rotate-180': isExpanded,
                      })}
                    />
                  </button>
                </div>
                {someHasError ? <GaSyncErrorMsg className="ml-11" /> : null}
              </div>
              {isExpanded ? (
                <div className="border border-solid border-t-0 border-mystic-500 flex flex-col w-full p-5 space-y-2.5">
                  {properties.map((p) => {
                    const isEnabled = enabledProperties.includes(p.id);

                    return (
                      <div
                        key={`p_${p.id}`}
                        className={classNames('flex flex-col w-full rounded', {
                          'border-2 p-[15px] border-dodger-blue-500': isEnabled,
                          'border p-4 border-mystic-500': !isEnabled,
                        })}
                      >
                        <Checkbox
                          checked={isEnabled}
                          checkBoxClass={classNames({
                            '!border-carnation-500': !!p.lastError,
                            '!bg-carnation-500': !!p.lastError && isEnabled,
                          })}
                          id={`p_${p.id}`}
                          label={p.name}
                          labelClass="-mt-px"
                          onChange={() => toggleProperty(p.id)}
                        />

                        {p.lastError ? <GaSyncErrorMsg className="ml-[30px]" /> : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <Modal.Actions>
        <Button
          disabled={didSubmit && isOverLimit}
          onClick={() => {
            if (isOverLimit) {
              if (!didSubmit) setDidSubmit(true);
              return;
            }
            if (!didSubmit) setDidSubmit(true);
            save();
          }}
        >
          {isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button variant="cancel" onClick={() => modal.close()}>
          Cancel
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

function GaSyncErrorMsg({ className }) {
  return (
    <span className={classNames('mt-2.5 text-error !text-sm leading-5', className)}>
      There was a problem syncing data between Google Analytics and Crazy Egg. If the problem persists,{' '}
      <SupportLink className="text-link !font-normal" href={SupportLinks.general.newSupportRequest}>
        contact Support for help.
      </SupportLink>
    </span>
  );
}
